import { 
  type InputHTMLAttributes, 
  type ReactNode, 
  forwardRef,
  useState 
} from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onRightIconClick?: () => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      onRightIconClick,
      className = '',
      type = 'text',
      id,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    
    const inputType = isPassword && showPassword ? 'text' : type;
    
    const togglePassword = () => setShowPassword(prev => !prev);
    
    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-slate-700 mb-1.5"
          >
            {label}
          </label>
        )}
        
        {/* Input container */}
        <div className="relative">
          {/* Left icon */}
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
              {leftIcon}
            </div>
          )}
          
          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            className={`
              block w-full rounded-xl border
              transition-all duration-200
              placeholder:text-slate-400
              focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
              disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
              ${leftIcon ? 'pl-11' : 'pl-4'}
              ${rightIcon || isPassword ? 'pr-11' : 'pr-4'}
              ${error 
                ? 'border-red-300 bg-red-50/50 focus:border-red-500 focus:ring-red-200' 
                : 'border-slate-200 bg-white hover:border-slate-300'
              }
              py-3 text-sm
              ${className}
            `}
            {...props}
          />
          
          {/* Right icon / Password toggle */}
          {(rightIcon || isPassword) && (
            <button
              type="button"
              onClick={isPassword ? togglePassword : onRightIconClick}
              className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
              tabIndex={-1}
            >
              {isPassword ? (
                <span className="material-symbols-outlined text-xl">
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              ) : (
                rightIcon
              )}
            </button>
          )}
        </div>
        
        {/* Error message */}
        {error && (
          <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">error</span>
            {error}
          </p>
        )}
        
        {/* Hint */}
        {hint && !error && (
          <p className="mt-1.5 text-xs text-slate-500">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
